import React, { useEffect, useState } from "react";
import { toast } from "../components/ui/sonner";
import { useApp } from "../context/AppContext";
import { Loader2, ArrowRightLeft, CheckCircle2, Clock, PackageCheck, Undo2 } from "lucide-react";

const Row = ({ compact, children, tid }) => <div className={`flex items-center gap-1.5 ${compact ? "text-[10px]" : "text-[11px]"}`} data-testid={tid}>{children}</div>;

/* 铃铛内联「交接确认」（双向）：部门经理指定接收人 → 接收人点「已接收」→ 完成；全部完成后自动勾掉离职流程交接任务 */
export const HandoverInline = ({ reminder, compact = false, onChanged }) => {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const T = (a, b) => (zh ? a : b);
  const tid = reminder.transitionId;
  const onlyPid = reminder.type === "hrms_handover_ack" ? reminder.projectId : null;
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");
  const [denied, setDenied] = useState(false);

  const load = () => api.get(`/hrms/transitions/${tid}/handover`).then((r) => setData(r.data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); setData({ pending: [], awaiting: [], done: [] }); });
  useEffect(() => { if (tid) load(); }, [tid]); // eslint-disable-line

  if (!tid || denied) return null;
  if (!data) return <p className="text-[10px] text-slate-400 mt-1"><Loader2 className="h-3 w-3 animate-spin inline" /></p>;
  const pick = (arr) => (onlyPid ? arr.filter((h) => h.id === onlyPid) : arr);
  const pend = pick(data.pending || []), wait = pick(data.awaiting || []), done = pick(data.done || []);
  const canAssign = !onlyPid && (data.canOverride || (data.assignees || []).length > 0) && !denied;
  const name = (hp) => hp.address || hp.clientName || hp.id;

  const assign = async (hp) => {
    const to = sel[hp.id];
    if (!to) return toast.error(T("请先选择接收人", "Pick a recipient"));
    setBusy(hp.id);
    try {
      await api.post(`/hrms/transitions/${tid}/handover/${hp.id}`, { handedTo: to, done: true });
      toast.success(T(`已指定交接给 ${to}，已发铃铛等待其「已接收」确认`, `Assigned to ${to}; waiting for acknowledgement`));
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.detailText || T("操作失败", "Failed")); } finally { setBusy(""); }
  };
  const undo = async (hp) => {
    setBusy(hp.id);
    try { await api.post(`/hrms/transitions/${tid}/handover/${hp.id}`, { done: false }); toast.success(T("已撤回指定", "Assignment withdrawn")); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.detailText || T("操作失败", "Failed")); } finally { setBusy(""); }
  };
  const ack = async (hp) => {
    setBusy(hp.id);
    try {
      const { data: r } = await api.post(`/hrms/transitions/${tid}/handover/${hp.id}/ack`, {});
      toast.success(r.allDone ? T("已接收 · 该员工全部项目交接完毕，离职流程交接任务已自动完成", "Received — all handovers complete, offboarding task auto-closed") : T("已确认接收", "Received"));
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.detailText || T("确认失败", "Failed")); } finally { setBusy(""); }
  };

  if (!pend.length && !wait.length) {
    return done.length > 0 ? (
      <p className="text-[10px] text-emerald-600 mt-1 inline-flex items-center gap-1" data-testid={`handover-alldone-${reminder.id}`}><CheckCircle2 className="h-3 w-3" />{onlyPid ? T(`「${name(done[0])}」已双向确认交接完成`, "Handover confirmed") : T(`${done.length} 个项目已全部双向确认交接`, `${done.length} project(s) handed over`)}</p>
    ) : null;
  }
  const btn = (extra) => `${compact ? "text-[10px] px-2 py-0.5" : "text-[11px] px-2.5 py-1"} rounded-full font-semibold disabled:opacity-40 inline-flex items-center gap-1 ${extra}`;
  return (
    <div className={`mt-1.5 rounded-lg border border-indigo-200 bg-indigo-50/50 ${compact ? "p-1.5" : "p-2.5"}`} data-testid={`handover-inline-${reminder.id}`} onClick={(e) => e.stopPropagation()}>
      <p className={`${compact ? "text-[10px]" : "text-[11px]"} font-semibold text-indigo-900 inline-flex items-center gap-1`}><ArrowRightLeft className="h-3 w-3" />
        {onlyPid ? T(`请确认接收 · ${data.employeeName}`, `Please acknowledge · ${data.employeeName}`) : T(`交接确认 · ${data.employeeName} · 待指定 ${pend.length} · 等待接收 ${wait.length}`, `Handover · ${data.employeeName} · ${pend.length} to assign · ${wait.length} awaiting`)}
      </p>
      <div className="space-y-1 mt-1">
        {pend.slice(0, compact ? 3 : 20).map((hp) => (
          <Row key={hp.id} compact={compact} tid={`handover-row-${reminder.id}-${hp.id}`}>
            <span className="text-slate-700 truncate flex-1 min-w-0" title={name(hp)}>{name(hp)}{hp.roles?.length ? <span className="text-slate-400"> · {hp.roles.join("/")}</span> : null}</span>
            {canAssign ? (
              <>
                <select data-testid={`handover-to-${reminder.id}-${hp.id}`} value={sel[hp.id] || ""} onChange={(e) => setSel((x) => ({ ...x, [hp.id]: e.target.value }))}
                  className={`${compact ? "h-6 text-[10px] max-w-[110px]" : "h-7 text-[11px] max-w-[160px]"} rounded border border-slate-300 bg-white px-1`}>
                  <option value="">{T("交接给…", "Hand to…")}</option>
                  {(data.assignees || []).map((u) => <option key={u.username} value={u.username}>{u.name}{u.region && u.region !== data.employeeBranch ? ` (${u.region})` : ""}</option>)}
                </select>
                <button type="button" data-testid={`handover-confirm-${reminder.id}-${hp.id}`} disabled={busy === hp.id || !sel[hp.id]} onClick={() => assign(hp)} className={btn("bg-indigo-600 hover:bg-indigo-700 text-white")}>
                  {busy === hp.id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}{T("指定交接", "Assign")}
                </button>
              </>
            ) : <span className="text-slate-400">{T("待部门经理指定", "Awaiting manager")}</span>}
          </Row>
        ))}
        {wait.slice(0, compact ? 3 : 20).map((hp) => {
          const mine = hp.handedTo === user?.username;
          return (
            <Row key={hp.id} compact={compact} tid={`handover-wait-${reminder.id}-${hp.id}`}>
              <span className="text-slate-700 truncate flex-1 min-w-0" title={name(hp)}>{name(hp)}<span className="text-slate-400"> → {hp.handedToName || hp.handedTo}</span></span>
              {mine || data.canOverride ? (
                <button type="button" data-testid={`handover-ack-${reminder.id}-${hp.id}`} disabled={busy === hp.id} onClick={() => ack(hp)} className={btn("bg-emerald-600 hover:bg-emerald-700 text-white")}>
                  {busy === hp.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <PackageCheck className="h-3 w-3" />}{mine ? T("已接收", "Received") : T("代确认接收", "Confirm on behalf")}
                </button>
              ) : <span className="inline-flex items-center gap-1 text-amber-600" data-testid={`handover-waiting-${reminder.id}-${hp.id}`}><Clock className="h-3 w-3" />{T("等待接收确认", "Awaiting ack")}</span>}
              {!mine && canAssign && <button type="button" data-testid={`handover-undo-${reminder.id}-${hp.id}`} disabled={busy === hp.id} onClick={() => undo(hp)} title={T("撤回指定", "Withdraw")} className="text-slate-400 hover:text-slate-700"><Undo2 className="h-3 w-3" /></button>}
            </Row>
          );
        })}
        {compact && pend.length + wait.length > 3 && <p className="text-[10px] text-slate-400">{T(`还有 ${pend.length + wait.length - 3} 个，前往提醒页处理`, `${pend.length + wait.length - 3} more in Reminders`)}</p>}
      </div>
    </div>
  );
};
